import { type DashboardSummary, SYNC_TRIGGER_LABELS } from '@acasa/shared'
import type { ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { Card } from '@/components/card'
import { PageHeader } from '@/components/page-header'
import { PageShell } from '@/components/page-shell'
import { useDashboard } from '@/features/dashboard/use-dashboard'
import { relativeTime } from '@/lib/relative-time'

/**
 * Übersicht — the state of the paperwork, in numbers.
 *
 * Every figure is counted server-side against the SAME scope the Unterlagen
 * table filters by (`CHECKLIST_SCOPE_LABEL`), and the scope is printed on the
 * card. A dashboard whose numbers disagree with the page behind it is worse
 * than no dashboard, and the fastest way to get there is to leave the reader
 * guessing what was counted.
 */
export function DashboardPage() {
  const { data, isLoading, isError } = useDashboard()

  return (
    <PageShell width="wide">
      <PageHeader
        title="Übersicht"
        description="Stand der Unterlagen, Protokolle und der onOffice-Synchronisierung."
      />

      {isError && (
        <p className="border-danger/40 bg-danger/10 text-danger mb-5 border px-3 py-2 text-sm">
          Die Übersicht konnte nicht geladen werden.
        </p>
      )}

      {/* Nothing rather than skeletons: this is four small cards, and a flash
          of placeholder boxes reads worse than a beat of empty page. */}
      {isLoading || !data ? null : <Summary data={data} />}
    </PageShell>
  )
}

function Summary({ data }: { data: DashboardSummary }) {
  const { documents, estates, handovers, imageAi, lastSync } = data
  const tracked = documents.open + documents.done + documents.notApplicable
  const resolved = documents.done + documents.notApplicable

  return (
    <div className="grid gap-4 sm:grid-cols-2">
      <Card title="Unterlagen" footer={`Gezählt wird: ${documents.scopeLabel}.`}>
        <div className="flex items-baseline gap-2">
          <span className="font-display text-ink text-[34px] leading-none tnum">
            {documents.open.toLocaleString('de-DE')}
          </span>
          <span className="text-muted text-sm">offene Dokumente</span>
        </div>

        <Bar done={resolved} total={tracked} />

        <dl className="mt-4">
          <Row label="Objekte mit offenen Punkten" value={documents.estatesWithOpen} />
          <Row label="Objekte vollständig" value={documents.estatesComplete} />
          <Row label="Erledigt" value={documents.done} />
          {/* Its own line, not folded into "erledigt": a document marked
              "nicht zutreffend" was a decision somebody made, and it reads
              differently from one that was actually delivered. */}
          <Row label="Nicht zutreffend" value={documents.notApplicable} />
          <Row label="Dokumentarten" value={documents.types} />
        </dl>

        <Link
          to="/checklists"
          className="text-brand hover:text-brand-strong mt-4 inline-block text-xs underline underline-offset-4 transition-colors"
        >
          Zur Matrix
        </Link>
      </Card>

      <Card
        title="onOffice-Synchronisierung"
        footer="Der lokale Spiegel ist ein Cache — onOffice bleibt die Quelle, gelesen wird ausschließlich."
      >
        {lastSync ? (
          <>
            <div className="flex items-baseline gap-2">
              <StatusDot status={lastSync.status} />
              <span className="text-ink text-sm">
                {SYNC_LABEL[lastSync.status]} · {SYNC_TRIGGER_LABELS[lastSync.trigger]}
              </span>
              <span className="text-faint text-xs">
                {relativeTime(lastSync.finishedAt ?? lastSync.startedAt)}
              </span>
            </div>

            <dl className="mt-4">
              <Row label="Objekte im Spiegel" value={estates.mirrored} />
              <Row label="davon im Prüfumfang" value={estates.inScope} />
              <Row label="Datensätze im letzten Lauf" value={lastSync.recordCount} />
            </dl>

            {lastSync.error && (
              <p className="border-danger/40 bg-danger/10 text-danger mt-4 border px-3 py-2 text-xs">
                {lastSync.error}
              </p>
            )}
          </>
        ) : (
          <p className="text-muted text-sm">
            Noch kein Abgleich gelaufen. Er startet täglich automatisch (Einstellungen) oder über
            „Aktualisieren“ auf der Unterlagen-Seite.
          </p>
        )}
      </Card>

      <Card title="Übergabeprotokolle" footer="Protokolle sind Originale — sie liegen nur hier.">
        <dl>
          <Row label="Entwürfe" value={handovers.draft} />
          <Row label="Abgeschlossen" value={handovers.completed} />
        </dl>

        <Link
          to="/protokolle"
          className="text-brand hover:text-brand-strong mt-4 inline-block text-xs underline underline-offset-4 transition-colors"
        >
          Zu den Protokollen
        </Link>
      </Card>

      <Card title="Bild-KI">
        {imageAi.configured ? (
          <dl>
            <Row label="In Arbeit" value={imageAi.active} />
            <Row label="Fertig" value={imageAi.succeeded} />
            <Row label="Fehlgeschlagen" value={imageAi.failed} />
          </dl>
        ) : (
          <p className="text-muted text-sm">
            Nicht konfiguriert — ohne hinterlegten Zugang zum Bilddienst bleibt die Funktion aus.
            Der Rest des Werkzeugs ist davon nicht betroffen.
          </p>
        )}
      </Card>
    </div>
  )
}

type SyncStatus = NonNullable<DashboardSummary['lastSync']>['status']

const SYNC_LABEL: Record<SyncStatus, string> = {
  running: 'Läuft gerade',
  succeeded: 'Erfolgreich',
  failed: 'Fehlgeschlagen',
}

function StatusDot({ status }: { status: SyncStatus }) {
  const tone =
    status === 'succeeded' ? 'bg-positive' : status === 'failed' ? 'bg-danger' : 'bg-warning'
  return <span className={`size-2 shrink-0 rounded-full ${tone}`} aria-hidden />
}

function Row({ label, value }: { label: string; value: number }) {
  return (
    <div className="border-line flex items-baseline justify-between gap-4 border-b py-1.5 last:border-0">
      <dt className="text-muted text-sm">{label}</dt>
      <dd className="text-ink tnum font-mono text-sm">{value.toLocaleString('de-DE')}</dd>
    </div>
  )
}

/**
 * Progress as a plain rule. The matrix encodes "done" as the solid mark because
 * most cells are outstanding; the same logic applies here, so the filled part
 * is what has been resolved rather than what is missing.
 */
function Bar({ done, total }: { done: number; total: number }): ReactNode {
  const percent = total === 0 ? 0 : Math.round((done / total) * 100)

  return (
    <div className="mt-4">
      <div className="bg-line h-[3px] w-full">
        <div className="bg-brand h-full" style={{ width: `${percent}%` }} />
      </div>
      <p className="text-faint mt-1.5 text-[11px]">
        {percent}% erledigt oder nicht zutreffend ({done.toLocaleString('de-DE')} von{' '}
        {total.toLocaleString('de-DE')})
      </p>
    </div>
  )
}
